import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Send } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fileUrl = (p) => (p?.startsWith("http") ? p : p ? `${BACKEND_URL}/api/files/${p}` : "");
const fmtTime = (t) => {
  try { return new Date(t).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export default function BrandChat() {
  const { token } = useParams();
  const [thread, setThread] = useState(null);
  const [err, setErr] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try { const r = await axios.get(`${API}/m/${token}/messages`); setMessages(r.data); } catch { /* ignore */ }
  }, [token]);

  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/m/${token}`); setThread(r.data); }
      catch { setErr("This chat link is invalid or expired."); }
    })();
    loadMessages();
    const t = setInterval(loadMessages, 5000);
    return () => clearInterval(t);
  }, [token, loadMessages]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/m/${token}/messages`, { body: input.trim() });
      setInput("");
      await loadMessages();
    } catch { toast.error("Could not send"); }
    finally { setSending(false); }
  };

  const onKey = (e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); };

  if (err) return <div className="min-h-screen bg-black grain text-white flex items-center justify-center"><div className="mono text-coral">{err.toUpperCase()}</div></div>;
  if (!thread) return <div className="min-h-screen bg-black grain text-white flex items-center justify-center"><div className="mono text-kinetic animate-pulse">LOADING…</div></div>;

  const c = thread.creator || {};

  return (
    <div className="min-h-screen bg-black grain text-white flex flex-col" data-testid="brand-chat">
      <header className="glass border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <a href="/" className="flex items-center gap-2"><div className="w-6 h-6 bg-kinetic" /><span className="font-extrabold tracking-tight">INDIEWOOD</span></a>
        <span className="mono text-white/40 hidden sm:inline">{(thread.category || "client").toUpperCase()} · CHAT</span>
      </header>

      <div className="max-w-2xl w-full mx-auto flex-1 flex flex-col px-4 py-6">
        <div className="flex items-center gap-4 border border-white/10 bg-[#0C0C0C] p-4 mb-4">
          <Avatar className="w-12 h-12 rounded-sm">
            <AvatarImage src={fileUrl(c.picture)} className="object-cover" />
            <AvatarFallback className="bg-white/10 rounded-sm">{c.name?.[0] || "C"}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-sans font-bold text-lg" data-testid="brand-chat-creator">{c.name}</div>
            {c.headline && <div className="mono text-white/40 truncate">{c.headline}</div>}
          </div>
          {c.slug && <a href={`/u/${c.slug}`} className="ml-auto mono text-white/50 hover:text-kinetic">VIEW PROFILE →</a>}
        </div>

        <div className="flex-1 border border-white/10 bg-[#0A0A0A] overflow-y-auto p-5 space-y-3 min-h-[40vh]" data-testid="brand-chat-messages">
          {messages.length === 0 && <div className="mono text-white/30 text-center py-16">START THE CONVERSATION WITH {(c.name || "THE CREATOR").toUpperCase()}</div>}
          {messages.map(m => {
            const mine = m.sender === "brand";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`brand-msg-${m.sender}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-sm ${mine ? "bg-kinetic text-black" : "bg-white/10 text-white"}`}>
                  <div className="mono text-[10px] opacity-60 mb-1">{mine ? "YOU" : (c.name || "CREATOR").toUpperCase()} · {fmtTime(m.created_at)}</div>
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.body}</div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <div className="border border-white/10 bg-[#0C0C0C] p-4 mt-4 space-y-3">
          <Textarea data-testid="brand-chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
            placeholder="Write a message… (⌘/Ctrl + Enter to send)" className="bg-black border-white/15 rounded-sm min-h-20" />
          <div className="flex justify-end">
            <Button data-testid="brand-chat-send" onClick={send} disabled={sending || !input.trim()} className="kinetic-btn rounded-sm">
              <Send className="w-4 h-4 mr-2" /> {sending ? "SENDING…" : "Send"}
            </Button>
          </div>
        </div>
        <div className="mono text-white/25 text-center mt-6">SECURED BY INDIEWOOD · THE INDIE MEGAPLEX · SAAS</div>
      </div>
    </div>
  );
}
